const STORAGE_KEY = 'atlasFeatures';
const LEGACY_KEY = 'settingsVersion';

export type FeatureName =
  | 'settings'
  | 'automatedPO'
  | 'purchaseOrder'
  | 'buyingCalendar'
  | 'inventoryTransfers'
  | 'scheduledReports'
  | 'boards'
  | 'productSegments'
  | 'reports'
  | 'plugins'
  | 'shopifyFlow'
  | 'googleSheetsReporting'
  | 'stockyMigration'
  | 'importingStatus'
  | 'downloadManager'
  | 'userManagement'
  | 'liveDemo';
export type FeatureVersion = 'atlas' | 'classic';

type FeaturesMap = Partial<Record<FeatureName, FeatureVersion>>;

/**
 * Per-feature route maps: Warbler path → Atlas path.
 * Used by VerticalMenuBar and router guards to redirect enabled features.
 */
export const ATLAS_FEATURE_LINKS: Record<FeatureName, Record<string, string>> = {
  // The billing surface (/subscription, /auto_subscription,
  // /subscription/confirm, /onboard-pricing) is deliberately NOT a feature here.
  // It was one (AS-10883..10886, default flipped to atlas in AS-10385) and was
  // taken back out so the install funnel and every plan change run on classic
  // only, with no stored choice or banner able to move them. The /assistant/*
  // billing routes in router/index.ts now redirect back to these classic paths.
  // Re-adding a `subscription` entry re-opens that switch -- do it on purpose.
  settings: {
    '/settings/contact': '/assistant/setup/store-contact',
    '/settings/reorder-parameters': '/assistant/setup/variants',
    '/settings/incoming-inventory': '/assistant/setup/incoming',
    '/settings/inventory-forecast': '/assistant/setup/forecasting',
    '/settings/bundle-product-mapping': '/assistant/setup/bundles',
    '/settings/multi-store-settings': '/assistant/setup/multistore',
    '/settings/location-restrictions': '/assistant/setup/locations',
    '/settings/purchase-order': '/assistant/setup/po-tax',
    '/settings/internal-inventory-transfer-settings': '/assistant/setup/transfers',
  },
  automatedPO: {
    '/automated-po-overview': '/assistant/automated-po',
    '/automation-configs': '/assistant/automated-po/rules',
    '/purchase-orders/unapproved': '/assistant/automated-po/approvals',
    '/automation-dashboard': '/assistant/automated-po',
  },
  purchaseOrder: {
    '/suppliers': '/assistant/suppliers',
    '/buying-table': '/assistant/buying-table',
    '/purchase-orders': '/assistant/purchase-order/list',
    '/purchase-orders/dashboard': '/assistant/purchase-order/insights',
  },
  // Buying Calendar has its OWN flag (not shared with purchaseOrder) so it can be
  // flipped independently. Flat path, no numeric detail route → no regex branch needed.
  buyingCalendar: {
    '/buying-calendar': '/assistant/buying-calendar',
  },
  inventoryTransfers: {
    '/inventory-transfers/suggestions': '/assistant/inventory-transfers',
  },
  // AS-10847. Two flat paths, no numeric detail route, so no regex branch is
  // needed in getAtlasRedirectTarget. The `?step=products` deep link used by the
  // Suppliers "Link Products from Stocky CSV" item rides along on its own:
  // atlasRedirectLocation() merges the incoming route query into the target.
  stockyMigration: {
    '/stocky-switcher': '/assistant/stocky-switcher',
    '/stocky-data-migration': '/assistant/stocky-data-migration',
  },
  scheduledReports: {
    '/schedules': '/assistant/scheduled-reports',
    '/schedule/templates': '/assistant/scheduled-reports/templates',
    '/schedule/profiles': '/assistant/scheduled-reports/profiles',
    // '/schedule/create' is deliberately NOT mapped: classic report pages hand a draft
    // to it via warbler-origin localStorage["schedule_to_create"], which the Atlas
    // origin cannot read.
    '/schedule/profile': '/assistant/scheduled-reports/profiles/new',
  },
  // Boards has its OWN flag so the Atlas dashboard rebuild rolls out (and
  // rolls back) independently (spec D9). `/board/:id` deep links are handled
  // by a numeric-id regex in getAtlasRedirectTarget, not this static map.
  boards: {
    '/my-dashboards': '/assistant/boards',
  },
  // Product Segments has its OWN flag so it can be flipped independently of the
  // purchaseOrder trio. The detail route carries the segment type as a path segment
  // in classic and as a query param in atlas — handled by a regex in
  // getAtlasRedirectTarget, not this static map.
  productSegments: {
    '/product-segment': '/assistant/product-segment',
    '/product/explore': '/assistant/product-segment/explore',
  },
  // Plugins is RETIRED, not migrated (AS-10788): the atlas targets render a
  // "no longer available" notice rather than a rebuilt marketplace. It gets its OWN
  // flag so the retirement can be rolled back on its own. `/plugin/:id` is a
  // numeric-id regex in getAtlasRedirectTarget, not this static map.
  plugins: {
    '/plugins': '/assistant/plugins',
  },
  // AS-10796: the Shopify Flow MANAGEMENT pages. Its own flag, deliberately not
  // folded into an existing one — `purchaseOrder` already covers Suppliers +
  // Purchase Orders + Buying Table together, so flipping one flips three, and
  // repeating that grouping for Integrations would rebuild the same trap.
  // All three paths are static, so no id regex is needed in
  // getAtlasRedirectTarget (unlike `plugins`), and the prefix match in
  // featureForClassicPath resolves switch-back for all of them.
  // NOTE: the Flow DELIVERY CHANNEL is a different thing and already ships on
  // atlas inside scheduled-reports — this flag does not affect it.
  shopifyFlow: {
    '/shopify-flow': '/assistant/shopify-flow',
    '/shopify-flow/events': '/assistant/shopify-flow/events',
    '/shopify-flow/templates': '/assistant/shopify-flow/templates',
  },
  // AS-10797: Google Sheets REPORTING (templates + generated files). Its own
  // flag for the same reason shopifyFlow has one — one FeatureName per
  // sub-integration, so retiring one classic surface never drags another with
  // it. The two `:id` routes are regex families in getAtlasRedirectTarget, not
  // static entries here.
  // NOTE: three unrelated Google Sheets things exist and none of them is this —
  // the `google_sheets` DELIVERY CHANNEL in scheduled-reports, the settings-sheet
  // plumbing under /assistant/setup, and the orphaned legacy /spreadsheet-*
  // routes (AS-10798). This flag governs only /google-spreadsheet-*.
  googleSheetsReporting: {
    '/google-spreadsheet-introduction': '/assistant/google-sheets',
    '/google-spreadsheet-templates': '/assistant/google-sheets/templates',
    '/google-spreadsheet-files': '/assistant/google-sheets/files',
  },
  // AS-10844: the import-status screen. Its own flag, per the same rule as
  // shopifyFlow/googleSheetsReporting — one FeatureName per screen, so this
  // never drags an unrelated surface with it.
  // BOTH classic paths are listed because they are two router entries onto ONE
  // component (router/index.ts:541,547) — mapping only /importing-status would
  // leave HeaderTitle.vue's /status-page link behind on classic.
  // Listing them here is also what repoints every other classic entry point:
  // VerticalMenuBar rewrites the five v1/v2 sidebar links out of
  // getActiveRouteMap(), and the router guard catches AppBanner.vue's
  // $router.push and SetupGuide.vue's <router-link>. No per-file edits needed,
  // and none wanted — a hand-edited link would be a second, un-flagged path.
  importingStatus: {
    '/importing-status': '/assistant/importing-status',
    '/status-page': '/assistant/importing-status',
  },
  // AS-10845. Flat path, no detail route — one static entry is enough, and it
  // repoints every entry point at once: VerticalMenuBar rewrites any menu link
  // found in getActiveRouteMap() (the seven menu configs), and the global guard
  // in router/index.ts runs getAtlasRedirectTarget on every navigation, which
  // covers HorizontalMenu.vue's <v-list-item link> and DownloadFullData.vue's
  // <router-link> alike. Editing those nine files individually would create a
  // second, un-flagged redirect path — the exact drift this map exists to stop.
  downloadManager: {
    '/download-manager': '/assistant/download-manager',
  },
  // Classic report page ⇄ AI Report Builder (classic-switch spec D1/D2), plus the
  // three BROWSING pages converted by AS-10382.
  //
  // AS-10382 shipped these three as UNCONDITIONAL routes instead (no flag, no stored
  // choice, no switch-back). AS-10871 is what that cost: warbler v2.130.0 carried the
  // redirect to production before atlas v2.158.0 carried the pages, and every merchant
  // opening My Workspace > Saved Reports got a 404 out of the embed with nothing to fall
  // back to. AS-10942 put them back on the flag — whose default is `classic` — so warbler
  // never depends on an atlas route existing.
  //
  // Report DETAIL is still absorbed by the builder through the numeric-id regex rules in
  // getAtlasRedirectTarget; those are untouched and ride the same flag.
  //
  // PRECEDENCE, and why this is safe: getAtlasRedirectTarget checks this map with an
  // EXACT key lookup (`getActiveRouteMap()[canonical]`) BEFORE the regex rules. So
  // `/my-reports` hits the entry below, while `/my-reports/123` misses it — no key equals
  // that string — and falls through to the D2 id regex. Turning any of these into a prefix
  // match would silently send every saved-report deep link to the list page instead of the
  // report. Covered by the precedence specs in
  // tests/unit/config/atlas-features-reports.spec.ts.
  reports: {
    '/reports': '/assistant/reports',
    '/report-library': '/assistant/report-library',
    '/my-reports': '/assistant/my-reports',
  },
  // AS-10388: User Management. Its OWN flag, per the one-FeatureName-per-screen
  // rule — `purchaseOrder` already covers three surfaces at once and flipping
  // one flips all three, which is the trap this avoids.
  //
  // This map governs the **v2** sidebar link only. v3 merchants have no sidebar
  // item for /users at all (replenishment-v3.ts lists it in
  // SETTINGS_HUB_COVERED), so they reach it through the atlas settings hub —
  // whose `User Management` entry is still a parentPath bounce back to this
  // classic page, deliberately, until AS-10867 (cross-merchant IDOR on six
  // users_controller endpoints) ships. Flipping this flag and dropping that
  // parentPath are two separate, deliberate steps.
  //
  // Flat path with no detail route, so no regex branch is needed in
  // getAtlasRedirectTarget, and featureForClassicPath resolves it from the
  // static key above.
  userManagement: {
    '/users': '/assistant/setup/users',
  },
  // AS-10388: Book Live Demo. Kept, not deprecated — it was invested in seven
  // months ago (96f43e69f, the Shopify-domain Calendly prefill) and is a
  // hardcoded top-level sidebar item for every tier (VerticalMenuBar.vue:68),
  // so it survives into v3.
  liveDemo: {
    '/live-demo': '/assistant/book-demo',
  },
};

/**
 * The classic category route `/reports/:type_id/:type_name` — browsing, so it follows the
 * three static entries in ATLAS_FEATURE_LINKS.reports and is gated by the same flag.
 *
 * Exactly two segments and a NUMERIC first one, so it cannot swallow
 * `/reports/:type_id/:type_name/:template_report_id` (three segments, detail),
 * `/reports/:id` (one segment, detail) or `/reports/measures/:code` (non-numeric).
 */
const CLASSIC_REPORT_CATEGORY_RE = /^\/reports\/(\d+)\/([^/]+)$/;

function migrateLegacyKey(): void {
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (legacy === null) return;
  localStorage.removeItem(LEGACY_KEY);

  if (legacy !== 'atlas' && legacy !== 'classic') return;

  const existing = readRaw();
  if (!existing.settings) {
    existing.settings = legacy;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  }
}

function readRaw(): FeaturesMap {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    return parsed as FeaturesMap;
  } catch {
    return {};
  }
}

export function getFeatures(): FeaturesMap {
  migrateLegacyKey();
  return readRaw();
}

// Per-feature fallback default. purchaseOrder stays classic until the Atlas
// supplier/PO suite is proven stable; settings/automatedPO keep their shipped atlas default.
const DEFAULT_VERSIONS: Record<FeatureName, FeatureVersion> = {
  settings: 'atlas',
  automatedPO: 'atlas',
  purchaseOrder: 'classic',
  buyingCalendar: 'classic',
  inventoryTransfers: 'classic',
  scheduledReports: 'atlas',
  boards: 'classic',
  // AS-10384: opt-in while the atlas Product Segments build goes through QA.
  productSegments: 'classic',
  reports: 'classic',
  // AS-10388: both opt-in. userManagement stays classic until AS-10867 (the
  // users_controller IDOR + password-hash leak) is fixed — the atlas tab calls
  // the same endpoints — and until manual QA signs off.
  userManagement: 'classic',
  liveDemo: 'classic',
  // AS-10788: opt-in until the retirement notice is signed off; flipping this is
  // what actually retires the classic marketplace for merchants.
  plugins: 'classic',
  // AS-10796: opt-in until the Flow management pages pass manual QA.
  shopifyFlow: 'classic',
  // AS-10797: opt-in until the Google Sheets reporting pages pass manual QA.
  googleSheetsReporting: 'classic',
  // AS-10847: atlas is the default for the Stocky migration.
  //
  // This is a DEFAULT, not a force: a merchant who has pressed "switch back to
  // classic" has a stored choice that still wins (see getFeatureVersion's
  // order), and both atlas Stocky pages carry ClassicSwitchBanner, so the way
  // back stays open. Flip this line to 'classic' to roll the whole thing back
  // without touching anything else.
  //
  // Must stay in step with atlas's own copy in
  // `frontend/src/lib/atlasFeatures.ts` — they are two copies of one contract,
  // and a mismatch makes the redirect and the switch-back banner disagree about
  // which experience the merchant is on.
  stockyMigration: 'atlas',
  // AS-10844: opt-in until the atlas import-status page passes manual QA.
  // Must stay in step with atlas's own DEFAULT_VERSIONS (frontend/src/lib/
  // atlasFeatures.ts) — the two files are separate copies of one contract, and
  // a mismatch means the switch-back banner and the redirect disagree about
  // which experience the merchant is on.
  importingStatus: 'classic',
  // AS-10845: opt-in until the atlas Download Manager passes manual QA.
  // Must stay in step with atlas's own DEFAULT_VERSIONS — they are two copies
  // of one contract, and a mismatch means the redirect and the switch-back
  // banner disagree about which experience the merchant is on.
  downloadManager: 'classic',
};

// Env overrides. Static literal keys → inlined by Vue CLI.
export function isFeatureForcedAtlas(feature: FeatureName): boolean {
  return feature === 'purchaseOrder'
    && process.env.VUE_APP_ATLAS_FORCE_PURCHASE_ORDER === 'true';
}

export function getDefaultVersion(feature: FeatureName): FeatureVersion {
  if (feature === 'purchaseOrder') {
    const env = process.env.VUE_APP_ATLAS_DEFAULT_PURCHASE_ORDER;
    if (env === 'atlas' || env === 'classic') return env;
  }
  if (feature === 'boards') {
    const env = process.env.VUE_APP_ATLAS_DEFAULT_BOARDS;
    if (env === 'atlas' || env === 'classic') return env;
  }
  if (feature === 'reports') {
    const env = process.env.VUE_APP_ATLAS_DEFAULT_REPORTS;
    if (env === 'atlas' || env === 'classic') return env;
  }
  if (feature === 'stockyMigration') {
    const env = process.env.VUE_APP_ATLAS_DEFAULT_STOCKY_MIGRATION;
    if (env === 'atlas' || env === 'classic') return env;
  }
  return DEFAULT_VERSIONS[feature];
}

export function getFeatureVersion(feature: FeatureName): FeatureVersion {
  if (isFeatureForcedAtlas(feature)) return 'atlas';
  const stored = getFeatures()[feature];
  if (stored === 'atlas' || stored === 'classic') return stored;
  return getDefaultVersion(feature);
}

export function setFeatureVersion(feature: FeatureName, version: FeatureVersion): void {
  const features = getFeatures();
  features[feature] = version;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
}

export function isAtlasEnabled(feature: FeatureName): boolean {
  return getFeatureVersion(feature) === 'atlas';
}

/**
 * Returns a flat route map of all currently enabled features.
 * Used by VerticalMenuBar and router guards.
 */
export function getActiveRouteMap(): Record<string, string> {
  const result: Record<string, string> = {};
  for (const feature of Object.keys(ATLAS_FEATURE_LINKS) as FeatureName[]) {
    if (isAtlasEnabled(feature)) {
      Object.assign(result, ATLAS_FEATURE_LINKS[feature]);
    }
  }
  return result;
}

const REPORT_BUILDER_PATH = '/assistant/ai-report-builder';

/**
 * Ids must be positive safe integers (classic-switch DC-B): Number() silently
 * rounds digit strings above MAX_SAFE_INTEGER, which would address the wrong report.
 */
export function isPositiveSafeId(raw: string | number | undefined | null): boolean {
  if (raw === undefined || raw === null) return false;
  const s = String(raw);
  if (!/^\d+$/.test(s)) return false;
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0;
}

/**
 * Resolve the Atlas redirect target for a classic path, or null if none.
 * Static map first, then numeric-id detail rules (only when purchaseOrder is
 * atlas-enabled). Never throws — any failure returns null (no redirect).
 */
/** True when a route query carries a non-empty classic `report_query` blob. */
function carriesReportQuery(query: RouteQuery | undefined): boolean {
  const blob = query?.report_query;
  return typeof blob === 'string' && blob.length > 0;
}

/** The shape vue-router hands a guard as `to.query` (values may be arrays). */
export type RouteQuery = Record<string, string | (string | null)[] | null | undefined>;

/**
 * Guard-side merge of a redirect target with the incoming route query. Keys the
 * target generated itself (`custom_report_id`, `template_report_id`, `system`)
 * win over same-named keys on the incoming link: vue-router applies an explicit
 * `query` AFTER the query parsed from `path`, so `/my-reports/123?custom_report_id=999`
 * would otherwise open report 999. Everything else on the link is forwarded as is.
 */
export function atlasRedirectLocation(
  target: string,
  incoming: RouteQuery | undefined,
): { path: string; query: RouteQuery } {
  const at = target.indexOf('?');
  const path = at === -1 ? target : target.slice(0, at);
  const query: RouteQuery = { ...(incoming ?? {}) };
  if (at !== -1) {
    new URLSearchParams(target.slice(at + 1)).forEach((value, key) => {
      query[key] = value;
    });
  }
  return { path, query };
}

/**
 * Drop one trailing slash so `/reports/` resolves like `/reports`.
 *
 * vue-router 3 matches these routes non-strictly, so `/reports/` navigates fine and
 * `to.path` KEEPS the slash. An exact-key map lookup and an anchored regex both miss it,
 * so an opted-in merchant following `/my-reports/` would land back on classic while
 * `/my-reports` redirected. `/` itself is left alone.
 *
 * Scope matters: this feeds the static-map lookup and the browsing category regex ONLY.
 * The numeric-id regexes keep matching the RAW path, because `/my-reports/123/` is not a
 * link anyone issues and DC-B rejects it deliberately — normalising there would turn that
 * rejection into a redirect.
 */
function withoutTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

export function getAtlasRedirectTarget(path: string, query?: RouteQuery): string | null {
  try {
    const canonical = withoutTrailingSlash(path);
    const staticTarget = getActiveRouteMap()[canonical];
    if (staticTarget) return staticTarget;

    if (isAtlasEnabled('purchaseOrder')) {
      const supplier = /^\/suppliers\/(\d+)$/.exec(path);
      if (supplier) return `/assistant/suppliers/${supplier[1]}`;
      const po = /^\/purchase-orders\/(\d+)$/.exec(path);
      if (po) return `/assistant/purchase-order/${po[1]}`;
    }

    if (isAtlasEnabled('productSegments')) {
      // /product-segment/:id/type/:type -> /assistant/product-segment/:id?type=:type
      const segment = /^\/product-segment\/(\d+)\/type\/([A-Za-z_]+)$/.exec(path);
      if (segment) return `/assistant/product-segment/${segment[1]}?type=${segment[2]}`;
    }

    if (isAtlasEnabled('scheduledReports')) {
      // Static map (checked above) handles the id-less pages; these numeric-id
      // regexes cover detail/edit deep links (digest emails, bookmarks).
      const scheduleEdit = /^\/schedule\/edit\/(\d+)(?:\/report\/\d+)?$/.exec(path);
      if (scheduleEdit) return `/assistant/scheduled-reports/${scheduleEdit[1]}/edit`;
      const profileEdit = /^\/schedule\/profile\/(\d+)$/.exec(path);
      if (profileEdit) return `/assistant/scheduled-reports/profiles/${profileEdit[1]}/edit`;
      const scheduleDetail = /^\/schedule\/(\d+)$/.exec(path);
      if (scheduleDetail) return `/assistant/scheduled-reports/${scheduleDetail[1]}`;
    }

    if (isAtlasEnabled('boards')) {
      // Warbler's /board/:id only ever serves public/system dashboards
      // (SystemDashboard.vue fetchPublic→fetchSystem), so the Atlas target is
      // the system-board read of the same id. The optional second segment is
      // warbler's legacy :type param — ignored on the Atlas side.
      const board = /^\/board\/(\d+)(?:\/[\w-]+)?$/.exec(path);
      if (board) return `/assistant/boards/${board[1]}?system=1`;

      // /myboard/:id is the merchant's own private board (DataWidget.vue push,
      // bookmarks, pinned tiles). No ?system=1 — Atlas defaults kind "mine".
      const myBoard = /^\/myboard\/(\d+)$/.exec(path);
      if (myBoard) return `/assistant/boards/${myBoard[1]}`;
    }

    if (isAtlasEnabled('plugins')) {
      // Warbler's /plugin/:id is the upsell destination FeatureLockedNotify links to;
      // it lands on the atlas retirement notice, which names the capability that id
      // used to unlock.
      const plugin = /^\/plugin\/(\d+)$/.exec(path);
      if (plugin) return `/assistant/plugins/${plugin[1]}`;
    }

    if (isAtlasEnabled('googleSheetsReporting')) {
      // A template's id in the URL is its backend FILENAME (e.g.
      // `sales_overview`), not a number — so this pattern is word characters,
      // not \d+. Getting that wrong would silently leave every template deep
      // link on the classic page while the list redirected.
      const template = /^\/google-spreadsheet-templates\/([\w.-]+)$/.exec(path);
      if (template) {
        return `/assistant/google-sheets/templates/${encodeURIComponent(template[1])}`;
      }
      // A generated file, by contrast, is a numeric primary key.
      const file = /^\/google-spreadsheet-files\/(\d+)$/.exec(path);
      if (file) return `/assistant/google-sheets/files/${file[1]}`;
    }

    if (isAtlasEnabled('reports')) {
      // Id routes always redirect (classic-switch D2); measure/analytics routes
      // redirect only when the link carries a report_query blob (D13) — the
      // builder cannot open a bare measure. A bare link still gets there one
      // beat later: the classic page mirrors its live state into
      // ?report_query (ReportPage.doReplaceReportQuery) and this guard fires on
      // that replace. The guard forwards to.query, so the blob rides along
      // verbatim (Phase A: blob wins, id kept).
      const measureOrAnalytics =
        /^\/reports\/measures\/[^/]+$/.test(path) || /^\/analytics\/[^/]+\/[^/]+$/.test(path);
      if (measureOrAnalytics && carriesReportQuery(query)) {
        return REPORT_BUILDER_PATH;
      }
      // The category route is BROWSING, so it follows the three static entries rather
      // than the builder. Tested against `canonical` for the same trailing-slash reason;
      // the anchored two-segment/numeric-first shape is what keeps it off the detail
      // forms, which are matched below against the raw path.
      const category = CLASSIC_REPORT_CATEGORY_RE.exec(canonical);
      if (category) return `/assistant/reports/${category[1]}/${category[2]}`;
      const custom = /^\/my-reports\/(\d+)$/.exec(path);
      if (custom && isPositiveSafeId(custom[1])) {
        return `${REPORT_BUILDER_PATH}?custom_report_id=${custom[1]}`;
      }
      const template =
        /^\/reports\/(\d+)$/.exec(path) || /^\/reports\/\d+\/[^/]+\/(\d+)$/.exec(path);
      if (template && isPositiveSafeId(template[1])) {
        return `${REPORT_BUILDER_PATH}?template_report_id=${template[1]}`;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * The classic supplier/PO views whose "Try the new version" banner switches to Atlas.
 * Detail views carry the entity id; list/dashboard/buying views are id-less.
 */
export type PoSwitchView =
  | 'suppliersList'
  | 'supplierDetail'
  | 'buyingTable'
  | 'purchaseOrderList'
  | 'poDashboard'
  | 'purchaseOrderDetail';

/**
 * The Atlas route a classic supplier/PO view navigates to when the user opts into the
 * new experience. Static targets are single-sourced from ATLAS_FEATURE_LINKS.purchaseOrder
 * so the forward switch and the reverse redirect can't drift; detail targets mirror
 * getAtlasRedirectTarget's numeric-id form. Pure + unit-tested, so a typo in a view's
 * switch target is caught here rather than only in the browser.
 */
export function purchaseOrderSwitchTarget(
  view: PoSwitchView,
  id?: string | number,
): string {
  const links = ATLAS_FEATURE_LINKS.purchaseOrder;
  switch (view) {
    case 'suppliersList':
      return links['/suppliers'];
    case 'supplierDetail':
      return `/assistant/suppliers/${id}`;
    case 'buyingTable':
      return links['/buying-table'];
    case 'purchaseOrderList':
      return links['/purchase-orders'];
    case 'poDashboard':
      return links['/purchase-orders/dashboard'];
    case 'purchaseOrderDetail':
      return `/assistant/purchase-order/${id}`;
  }
}

/**
 * The classic Product Segment views whose "Try the new version" banner switches to Atlas.
 * The detail view carries the segment id AND its type (the classic route puts the type in
 * the path; Atlas takes it as a query param).
 */
export type ProductSegmentSwitchView =
  | 'segmentList'
  | 'segmentExplore'
  | 'segmentDetail';

/**
 * The Atlas route a classic Product Segment view navigates to when the user opts into the
 * new experience. Static targets are single-sourced from ATLAS_FEATURE_LINKS.productSegments
 * so the forward switch and the reverse redirect can't drift; the detail target mirrors the
 * `/product-segment/:id/type/:type` branch of getAtlasRedirectTarget. Pure + unit-tested.
 */
export function productSegmentSwitchTarget(
  view: ProductSegmentSwitchView,
  id?: string | number,
  type?: string,
): string {
  const links = ATLAS_FEATURE_LINKS.productSegments;
  switch (view) {
    case 'segmentList':
      return links['/product-segment'];
    case 'segmentExplore':
      return links['/product/explore'];
    case 'segmentDetail':
      // The type is always present on the classic route, but a bookmark or a partially
      // loaded model can leave it empty — fall back to the id-only form rather than
      // emitting `?type=undefined`, which Atlas would read as a real segment type.
      return type
        ? `/assistant/product-segment/${id}?type=${type}`
        : `/assistant/product-segment/${id}`;
  }
}
/**
 * The atlas route the classic import-status page navigates to on "Try the new
 * version". Single-sourced from ATLAS_FEATURE_LINKS so the forward switch and
 * the reverse redirect cannot drift. The screen has no id routes, so unlike the
 * supplier/PO/segment helpers it takes no arguments. Pure + unit-tested.
 */
export function importingStatusSwitchTarget(): string {
  return ATLAS_FEATURE_LINKS.importingStatus['/importing-status'];
}

/**
 * The atlas route the classic My Reports page navigates to on "Try the new version"
 * (AS-10942). Single-sourced from ATLAS_FEATURE_LINKS so the forward switch and the
 * reverse redirect cannot drift: if the atlas route is ever renamed, the map entry and
 * this target move together. A hardcoded literal here would land the merchant on a 404
 * with the flag ALREADY set to atlas — AS-10871's failure mode, and this time with no
 * classic page left to fall back to. The list page has no id, so it takes no arguments.
 *
 * Distinct from `reportsSwitchTarget` below, which is the DETAIL page's banner and
 * carries the saved-report identity into the builder.
 */
export function myReportsSwitchTarget(): string {
  return ATLAS_FEATURE_LINKS.reports['/my-reports'];
}

/**
 * The atlas route the classic Report Library navigates to on "Try the new version".
 * Same single-sourcing rule as `myReportsSwitchTarget`.
 *
 * This page needs its OWN banner even though it shares the `reports` flag with My Reports:
 * a merchant standing on the Report Library has no reason to detour via My Reports to find
 * the way forward, and the flag being shared is invisible to them. Gating the whole
 * browsing set behind one page's banner looked tidy in the plan and was wrong in the hand.
 */
export function reportLibrarySwitchTarget(): string {
  return ATLAS_FEATURE_LINKS.reports['/report-library'];
}

/**
 * The atlas route the classic Browse Reports catalog navigates to on "Try the new
 * version". Same single-sourcing rule, and the same reason it exists at all: all three
 * browsing pages share the `reports` flag, and all three need their own way forward.
 */
export function browseReportsSwitchTarget(): string {
  return ATLAS_FEATURE_LINKS.reports['/reports'];
}

/** The saved-report identity a classic report page holds (route params). */
export type ReportSwitchIdentity =
  | { kind: 'custom' | 'template'; id: string | number }
  | null;

/**
 * The builder URL a classic report page navigates to on "Try the new version"
 * (classic-switch spec D4): the saved-report id when the route has one, plus
 * the page's fresh generateReportQuery() blob so the builder shows exactly
 * the same report. Param names come from Phase A (classicReport.ts). Pure —
 * a null/empty blob (generation failed) still switches by id; no id and no
 * blob lands on the builder home.
 */
export function reportsSwitchTarget(
  identity: ReportSwitchIdentity,
  reportQuery?: string | null,
): string {
  const params = new URLSearchParams();
  if (identity && isPositiveSafeId(identity.id)) {
    params.set(identity.kind === 'custom' ? 'custom_report_id' : 'template_report_id', String(identity.id));
  }
  if (reportQuery) params.set('report_query', reportQuery);
  const qs = params.toString();
  return qs ? `${REPORT_BUILDER_PATH}?${qs}` : REPORT_BUILDER_PATH;
}

/**
 * Which feature a classic path belongs to — used by AtlasEmbed's explicit
 * "Switch back to classic view" handling to flip that feature to classic
 * (classic-switch spec D6). Static maps first (exact or `key + '/'` prefix, in
 * ATLAS_FEATURE_LINKS order — today's loop, byte-for-byte), then the regex
 * families whose ids never appear in the static maps. Null when nothing
 * matches; never throws.
 */
export function featureForClassicPath(path: string): FeatureName | null {
  try {
    const normalized = path.split('?')[0].split('#')[0];
    for (const feature of Object.keys(ATLAS_FEATURE_LINKS) as FeatureName[]) {
      const warblerPaths = Object.keys(ATLAS_FEATURE_LINKS[feature]);
      if (warblerPaths.some(wp => normalized === wp || normalized.startsWith(wp + '/'))) {
        return feature;
      }
    }
    const families: Array<[FeatureName, RegExp[]]> = [
      ['reports', [
        /^\/my-reports\/\d+$/,
        /^\/reports\/\d+$/,
        /^\/reports\/\d+\/[^/]+\/\d+$/,
        /^\/reports\/measures\/[^/]+$/,
        /^\/analytics\/[^/]+\/[^/]+$/,
      ]],
      ['purchaseOrder', [/^\/suppliers\/\d+$/, /^\/purchase-orders\/\d+$/]],
      ['scheduledReports', [
        /^\/schedule\/edit\/\d+(?:\/report\/\d+)?$/,
        /^\/schedule\/profile\/\d+$/,
        /^\/schedule\/\d+$/,
      ]],
      ['boards', [/^\/board\/\d+(?:\/[\w-]+)?$/, /^\/myboard\/\d+$/]],
      ['plugins', [/^\/plugin\/\d+$/]],
      // googleSheetsReporting needs no family entry: its static keys
      // (/google-spreadsheet-templates, /google-spreadsheet-files) are exact
      // prefixes of both detail routes, so the startsWith match above already
      // resolves them. `plugins` needs one only because /plugin/:id is not a
      // prefix of /plugins.
    ];
    for (const [feature, patterns] of families) {
      if (patterns.some(re => re.test(normalized))) return feature;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Route names (src/router/index.ts) of the classic report pages that carry the
 * "Try the new version" banner (classic-switch spec D3). Schedule-edit, card-view
 * and scheduled-preview pages carry side flows the builder does not model.
 */
const REPORTS_BANNER_ROUTES: ReadonlySet<string> = new Set([
  'customReport',
  'templateReport',
  'templateReportByType',
  'detailReportPage',
  'detailReportByName',
]);

/**
 * Whether the classic report page shows the reports switch banner: only on the
 * banner routes and only while the report is not behind a wall (permission lock
 * or data-readiness lock). Deliberately NOT gated on the stored version: id
 * routes are redirected before this page renders once atlas is chosen, and
 * measure/analytics routes never redirect, so hiding the banner there would
 * strand an atlas-chosen merchant on classic with no way into the builder. Pure.
 */
export function showReportsSwitchBanner(input: {
  routeName: string | null | undefined;
  locked: boolean;
}): boolean {
  if (input.locked) return false;
  return REPORTS_BANNER_ROUTES.has(input.routeName ?? '');
}
